import * as React from 'react';

import {NutAnalyticsClient} from 'nutshell-core/nut-analytics-client';
import type {AnalyticsConstant} from 'nutshell-core/nut-analytics-client';

import {Modal} from '../modal/modal';
import {OnboardingPage} from '../onboarding-page/onboarding-page';

import type {PageChangeDirection} from './types';

export type OnboardingPageType = {
    id: string;
    body: React.ReactNode;
    art: {
        background: string;
        backgroundGradient?: string;
        image: React.ComponentType<{
            direction: PageChangeDirection;
        }>;
    };
    nextDisabled?: boolean;
    secondaryButtonDisabled?: boolean;
    stepBackwardsDisabled?: boolean;
    nextButtonText?: string;
    secondaryButtonText?: string;
    onClickNextButton?: () => void;
    onClickSecondaryButton?: () => void;
};

type Props = JSX.LibraryManagedAttributes<typeof Modal, React.ComponentProps<typeof Modal>> & {
    onClose: () => void;
    onFinish: (results: {secondsViewed: number}) => void;
    firstButtonText?: string;
    nextButtonText?: string;
    lastButtonText?: string;
    lastButtonDisabled?: boolean;
    analyticsClient?: NutAnalyticsClient;
    getAnalyticsEventForPageView?: (pageNum: number) => AnalyticsConstant | null | undefined;
    pages: OnboardingPageType[];
    pageAutoAdvance?: boolean;
    pageAutoPrevious?: boolean;
    maxHeight?: number;
};

export function OnboardingModal(props: Props) {
    const {
        pages,
        firstButtonText = 'Next',
        nextButtonText = 'Next',
        lastButtonText = 'Ok!',
        lastButtonDisabled,
        analyticsClient,
        getAnalyticsEventForPageView,
        onClose,
        onFinish,
        maxHeight = 500,
        pageAutoAdvance,
        pageAutoPrevious,
        ...otherProps
    } = props;

    const pageViewStartTime = React.useRef<Date>(new Date());
    const [currentPage, setCurrentPage] = React.useState<number>(0);
    const [direction, setDirection] = React.useState<PageChangeDirection>('forward');
    const [hasAutoAdvanced, setHasAutoAdvanced] = React.useState<boolean>(false);
    const [hasAutoPrevious, setHasAutoPrevious] = React.useState<boolean>(false);

    const handlePageChange = (newPageNum: number) => {
        // out of all the pages, if there is a page that has nextDisabled, and if the newPageNum is greater than that page, then do not change the page
        const nextDisabledPage = pages.find((page) => page.nextDisabled);
        const nextDisabledPageIndex = pages.findIndex((page) => page.nextDisabled);

        // if the secondary button is disabled, then do not allow going backwards
        const secondaryButtonDisabledPageIndex = pages.findIndex(
            (page) => page.secondaryButtonDisabled
        );

        // if the breadcrumb buttons are disabled, do not allow going backwards
        const stepBackwardsDisabledPageIndex = pages.findIndex(
            (page) => page.stepBackwardsDisabled
        );

        if (
            (secondaryButtonDisabledPageIndex === currentPage &&
                newPageNum <= secondaryButtonDisabledPageIndex) ||
            (stepBackwardsDisabledPageIndex <= currentPage &&
                newPageNum <= stepBackwardsDisabledPageIndex)
        ) {
            return;
        }

        if (!nextDisabledPage || newPageNum <= nextDisabledPageIndex) {
            setCurrentPage(newPageNum);
            setDirection('jump');
        }
    };

    const handlePrevPage = React.useCallback(() => {
        setCurrentPage(currentPage - 1);
        setDirection('back');
    }, [currentPage]);

    const handleNextPage = React.useCallback(() => {
        // @ts-expect-error
        if (pages[currentPage].onClickNextButton) {
            // @ts-expect-error
            pages[currentPage].onClickNextButton();
        }
        setCurrentPage(currentPage + 1);
        setDirection('forward');
    }, [currentPage, pages]);

    const handleKeypress = React.useCallback(
        (e: KeyboardEvent) => {
            const isFirstPage = currentPage === 0;
            const isLastPage = currentPage === pages.length - 1;
            if (e.key === 'ArrowRight' && !isLastPage) {
                // @ts-expect-error
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                pages[currentPage].nextDisabled ? undefined : handleNextPage();
            }

            if (e.key === 'ArrowLeft' && !isFirstPage) {
                handlePrevPage();
            }
        },
        [currentPage, pages, handleNextPage, handlePrevPage]
    );

    const handleClose = () => {
        if (analyticsClient && getAnalyticsEventForPageView) {
            // Record how long the user was on the final page before this unmounted
            // @ts-expect-error
            const durationSecs = Math.floor((new Date() - pageViewStartTime.current) / 1000);
            const event = getAnalyticsEventForPageView(currentPage);

            if (event) {
                analyticsClient.track(event, {durationSecs});
            }
        }

        onClose();
    };

    const handleFinish = () => {
        // @ts-expect-error
        if (pages[currentPage].onClickNextButton) {
            // @ts-expect-error
            pages[currentPage].onClickNextButton();
        }

        // @ts-expect-error
        const secondsViewed = Math.floor((new Date() - pageViewStartTime.current) / 1000);
        onFinish({secondsViewed});

        handleClose();
    };

    React.useEffect(() => {
        // Auto-advance to the next page if enabled and we haven't already done it
        if (pageAutoAdvance && !hasAutoAdvanced) {
            handleNextPage();
            setHasAutoAdvanced(true);
        }
        if (pageAutoPrevious && !hasAutoPrevious) {
            handlePrevPage();
            setHasAutoPrevious(true);
        }
    }, [
        pageAutoAdvance,
        pageAutoPrevious,
        hasAutoAdvanced,
        hasAutoPrevious,
        handleNextPage,
        handlePrevPage,
    ]);

    React.useEffect(() => {
        // Record how long the user was on the previous page
        if (analyticsClient && getAnalyticsEventForPageView) {
            // @ts-expect-error
            const durationSecs = Math.floor((new Date() - pageViewStartTime.current) / 1000);
            pageViewStartTime.current = new Date();
            const event = getAnalyticsEventForPageView(currentPage);
            if (event) {
                analyticsClient.track(event, {durationSecs});
            }
        }
    }, [currentPage, analyticsClient, getAnalyticsEventForPageView]);

    React.useEffect(() => {
        document.addEventListener('keydown', handleKeypress);

        return () => {
            document.removeEventListener('keydown', handleKeypress);
        };
    }, [handleKeypress]);

    // @ts-expect-error
    const ImageComponent = pages[currentPage].art.image;

    // @ts-expect-error
    const currentPageNextButtonText = pages[currentPage].nextButtonText;

    let buttonText = '';
    if (currentPageNextButtonText) {
        buttonText = currentPageNextButtonText;
    } else if (currentPage === 0) {
        buttonText = firstButtonText;
    } else {
        buttonText = nextButtonText;
    }

    return (
        <Modal
            isOpen={true}
            onClose={handleClose}
            noPadding={true}
            maxHeight={maxHeight}
            canClickOutsideToClose={true}
            {...otherProps}
        >
            <OnboardingPage
                type='multiPage'
                // @ts-expect-error
                bodyComponent={pages[currentPage].body}
                // @ts-expect-error
                bodyId={pages[currentPage].id}
                // @ts-expect-error
                artBackgroundColor={pages[currentPage].art.background}
                // @ts-expect-error
                artBackgroundGradient={pages[currentPage].art.backgroundGradient}
                artImage={<ImageComponent direction={direction} />}
                buttonText={buttonText}
                // @ts-expect-error
                secondaryButtonText={pages[currentPage].secondaryButtonText}
                currentPage={currentPage}
                pageCount={pages.length}
                // @ts-expect-error
                secondaryButtonDisabled={pages[currentPage].secondaryButtonDisabled}
                finalButtonText={currentPageNextButtonText || lastButtonText}
                finalButtonDisabled={lastButtonDisabled}
                // @ts-expect-error
                nextButtonDisabled={pages[currentPage].nextDisabled}
                // @ts-expect-error
                isFullWidth={pages[currentPage].isFullWidth}
                onNext={handleNextPage}
                onGotoPage={handlePageChange}
                onClose={handleClose}
                onFinish={handleFinish}
                // @ts-expect-error
                onClickSecondaryButton={pages[currentPage].onClickSecondaryButton}
            />
        </Modal>
    );
}
